import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { AuthService } from "./auth.service";
import { NotificationService } from "./notification";
import { AuthProfile } from "./models/auth.model";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-kicgle-id-card",
  imports: [CommonModule, MatIconModule],
  styles: [
    `
      .fingerprint-icon-vertical {
        transform: none !important;
        -webkit-transform: none !important;
        transform-origin: center !important;
        rotate: 0deg !important;
        transform-box: fill-box !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
    `,
  ],
  template: `
    @if (isOpen()) {
      <!-- Pop-up Backdrop with high z-index (z-[1000]) -->
      <div
        class="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-5 md:p-6 overflow-y-auto bg-black/65 dark:bg-black/80 backdrop-blur-md animate-ios-fade"
        (click)="onBackdropClick($event)"
        (keydown.escape)="closed.emit()"
        tabindex="0"
      >
        <div
          class="relative w-full max-w-md sm:max-w-lg md:max-w-2xl flex flex-col items-center my-auto"
          (click)="$event.stopPropagation()"
        >
          <!-- Top Section: Download PDF and Red Close Buttons -->
          <div class="w-full flex items-center justify-between gap-2 mb-2 sm:mb-2.5 px-0.5 animate-ios-slide">
            <!-- Download 2-Page Standard ID Card PDF Button -->
            <button
              type="button"
              (click)="downloadPdf()"
              [disabled]="isGeneratingPdf()"
              class="h-10 px-3.5 sm:px-4 rounded-full bg-ios-blue hover:bg-ios-blue/90 active:scale-95 text-white shadow-md hover:shadow-lg flex items-center gap-2 text-xs font-black transition-all cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
              title="Download Two-Page Standard ID Card PDF"
            >
              @if (isGeneratingPdf()) {
                <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span class="hidden sm:inline">Generating PDF...</span>
              } @else {
                <mat-icon class="!text-[18px]">picture_as_pdf</mat-icon>
                <span>Download PDF</span>
              }
            </button>

            <!-- Prominent Red Close Button -->
            <button
              type="button"
              (click)="closed.emit()"
              class="w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 active:scale-90 text-white shadow-[0_4px_16px_rgba(239,68,68,0.5)] hover:shadow-[0_6px_20px_rgba(239,68,68,0.6)] flex items-center justify-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-400 z-[320]"
              aria-label="Close ID Card"
              title="Close"
            >
              <mat-icon class="!text-2xl font-bold leading-none">close</mat-icon>
            </button>
          </div>

          <!-- Navigation Tabs: Front, Back, and Preview (Positioned below Download PDF & Close, above ID card) -->
          <div class="w-full flex items-center justify-center sm:justify-start mb-3 px-0.5 animate-ios-slide">
            <div class="flex items-center gap-1 p-1 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 shadow-md">
              <button
                type="button"
                (click)="activeSide.set('front')"
                class="px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer flex items-center gap-1.5"
                [class.bg-white]="activeSide() === 'front'"
                [class.text-slate-900]="activeSide() === 'front'"
                [class.shadow-sm]="activeSide() === 'front'"
                [class.text-white]="activeSide() !== 'front'"
                [class.hover:bg-white/10]="activeSide() !== 'front'"
                title="View Front (Page 1)"
              >
                <mat-icon class="!text-[16px]">badge</mat-icon>
                <span>Front</span>
              </button>
              <button
                type="button"
                (click)="activeSide.set('back')"
                class="px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer flex items-center gap-1.5"
                [class.bg-white]="activeSide() === 'back'"
                [class.text-slate-900]="activeSide() === 'back'"
                [class.shadow-sm]="activeSide() === 'back'"
                [class.text-white]="activeSide() !== 'back'"
                [class.hover:bg-white/10]="activeSide() !== 'back'"
                title="View Back (Page 2)"
              >
                <mat-icon class="!text-[16px]">verified</mat-icon>
                <span>Back</span>
              </button>
              <button
                type="button"
                (click)="activeSide.set('preview')"
                class="px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer flex items-center gap-1.5"
                [class.bg-white]="activeSide() === 'preview'"
                [class.text-slate-900]="activeSide() === 'preview'"
                [class.shadow-sm]="activeSide() === 'preview'"
                [class.text-white]="activeSide() !== 'preview'"
                [class.hover:bg-white/10]="activeSide() !== 'preview'"
                title="View ID Card Preview"
              >
                <mat-icon class="!text-[16px]">auto_stories</mat-icon>
                <span>Preview</span>
              </button>
            </div>
          </div>

          <!-- ACTIVE CARD DISPLAY / PDF PREVIEW -->
          <div class="w-full relative">
            @if (activeSide() === 'front') {
              <!-- PAGE ONE: Exact Replica of Current Front ID Card Layout -->
              <div
                class="relative w-full rounded-2xl sm:rounded-3xl pt-3.5 sm:pt-4 md:pt-4.5 px-5 sm:px-6 md:px-7 pb-4 sm:pb-5 md:pb-6 bg-white border border-slate-200/90 shadow-2xl overflow-hidden animate-ios-slide select-none z-[310] flex flex-col justify-between"
                role="dialog"
                aria-modal="true"
                aria-label="Kicgle Profile ID Card - Front"
              >
                <!-- HEADER ROW: Left-Aligned Logo, Centered Barcode, Large Prominent Material Fingerprint Icon in Top Right Corner (replaces contactless payment icon) -->
                <div class="relative z-10 flex items-center justify-between gap-2 sm:gap-4 pb-2 sm:pb-2.5 border-b border-slate-100">
                  <!-- Left: Kicgle Logo -->
                  <div class="shrink-0 flex items-center">
                    <img
                      src="kicgle.svg"
                      alt="Kicgle"
                      class="h-7 sm:h-8 w-auto object-contain filter drop-shadow-xs"
                      referrerpolicy="no-referrer"
                    />
                  </div>

                  <!-- Center: Dynamic Barcode and 17-digit Kicgle ID -->
                  <div class="flex-1 flex flex-col items-center justify-center min-w-0 px-1 sm:px-2">
                    <div class="w-full max-w-[180px] sm:max-w-[210px] flex flex-col items-center">
                      <svg
                        [attr.viewBox]="'0 0 ' + barcodeData().totalWidth + ' 22'"
                        class="h-4 sm:h-5 w-full text-zinc-900"
                        preserveAspectRatio="none"
                      >
                        @for (bar of barcodeData().bars; track $index) {
                          <rect
                            [attr.x]="bar.x"
                            y="0"
                            [attr.width]="bar.width"
                            height="22"
                            fill="#18181b"
                          />
                        }
                      </svg>
                      <span
                        class="font-mono text-[9px] sm:text-[10px] font-bold tracking-[0.1em] text-zinc-600 mt-0.5 whitespace-nowrap text-center block w-full select-all"
                      >
                        {{ formattedKicgleId() }}
                      </span>
                    </div>
                  </div>

                  <!-- Top Right Corner: Large Prominent Clean Material Design Fingerprint Icon -->
                  <div class="shrink-0 flex items-center justify-end">
                    <mat-icon
                      class="fingerprint-icon-vertical !text-[34px] sm:!text-[38px] !w-[34px] !h-[34px] sm:!w-[38px] sm:!h-[38px] text-ios-blue select-none flex items-center justify-center leading-none"
                      aria-label="Biometric Fingerprint Verified"
                    >
                      fingerprint
                    </mat-icon>
                  </div>
                </div>

                <!-- BODY SECTION: Perfect Circle Profile Photo + NAME and DATE OF BIRTH (without colons) -->
                <div class="relative z-10 flex flex-row items-center gap-4 sm:gap-6 py-3.5 sm:py-4.5 my-auto">
                  <!-- Left: Profile Photo as a Perfect Circle -->
                  <div class="shrink-0">
                    <div
                      class="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-slate-200/90 shadow-md bg-slate-50 flex items-center justify-center relative"
                    >
                      @if (photoDataUrl() || photoURL()) {
                        <img
                          [src]="photoDataUrl() || photoURL()"
                          [alt]="displayName()"
                          class="w-full h-full object-cover rounded-full"
                          referrerpolicy="no-referrer"
                        />
                      } @else {
                        <div
                          class="w-full h-full flex items-center justify-center bg-gradient-to-tr from-ios-blue to-purple-600 text-white text-xl sm:text-2xl md:text-3xl font-black rounded-full"
                        >
                          {{ initial() }}
                        </div>
                      }
                    </div>
                  </div>

                  <!-- Right: Labels 'NAME' and 'DATE OF BIRTH' without colons -->
                  <div class="flex-1 flex flex-col justify-center min-w-0 text-left">
                    <!-- Name Section -->
                    <div class="mb-2.5 sm:mb-3">
                      <span
                        class="block text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500"
                      >
                        NAME
                      </span>
                      <span
                        class="block text-base sm:text-lg md:text-xl font-extrabold text-slate-900 tracking-tight leading-snug break-words mt-0.5"
                      >
                        {{ displayName() }}
                      </span>
                    </div>

                    <!-- Date of Birth Section -->
                    <div>
                      <span
                        class="block text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500"
                      >
                        DATE OF BIRTH
                      </span>
                      <span
                        class="block text-sm sm:text-base md:text-lg font-bold text-slate-800 tracking-tight leading-snug whitespace-nowrap mt-0.5"
                      >
                        {{ formattedDob() }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- FOOTER: Exactly Three Neatly Aligned Lines -->
                <div
                  class="relative z-10 pt-2.5 sm:pt-3 border-t border-slate-100 w-full text-center"
                >
                  <div
                    class="text-[9px] sm:text-[10px] md:text-[10.5px] font-semibold text-zinc-600 leading-normal space-y-0.5 sm:space-y-1"
                  >
                    <div>
                      Learn clearly (<span class="text-ios-blue font-bold">Keen</span>), Think innovatively (<span class="text-ios-red font-bold">Idea</span>)
                    </div>
                    <div>
                      Be determinate (<span class="text-ios-orange font-bold">Core</span>), Act fast (<span class="text-ios-purple font-bold">Go</span>), Plan properly (<span class="text-ios-green font-bold">Lead</span>)
                    </div>
                    <div>
                      Delivery everlasting quality (<span class="text-ios-pink font-bold">Ever</span>, <span class="text-amber-500 font-bold tracking-wide underline decoration-amber-400 decoration-1 underline-offset-2 ml-0.5">SUCCESS IS CERTAIN</span>)
                    </div>
                  </div>
                </div>
              </div>
            } @else if (activeSide() === 'back') {
              <!-- PAGE TWO: Feature the Kicgle Logo Prominently Centered in the Same ID Card Size -->
              <div
                class="relative w-full rounded-2xl sm:rounded-3xl p-5 sm:p-7 md:p-8 bg-white border border-slate-200/90 shadow-2xl overflow-hidden animate-ios-slide select-none z-[310] flex flex-col items-center justify-between min-h-[260px] sm:min-h-[290px] md:min-h-[310px]"
                role="dialog"
                aria-modal="true"
                aria-label="Kicgle Profile ID Card - Back"
              >
                <!-- Subtle Top Header Label -->
                <div class="w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-slate-400 pb-2 border-b border-slate-100">
                  <div class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5 text-ios-blue shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                    </svg>
                    <span>Official Identity Card</span>
                  </div>
                  <span>Card No: {{ formattedKicgleId() }}</span>
                </div>

                <!-- Prominently Centered Kicgle Logo -->
                <div class="my-auto py-6 flex flex-col items-center justify-center">
                  <img
                    src="kicgle.svg"
                    alt="Kicgle Logo"
                    class="w-48 sm:w-60 md:w-72 h-auto object-contain filter drop-shadow-sm transition-transform duration-300 hover:scale-105"
                    referrerpolicy="no-referrer"
                  />
                  <p class="mt-3 text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500 text-center">
                    Universal Digital Identity
                  </p>
                </div>

                <!-- Subtle Bottom Footer Strip -->
                <div class="w-full pt-2.5 border-t border-slate-100 flex items-center justify-between text-[9px] sm:text-[10px] font-bold text-slate-400">
                  <span>Issued for: {{ displayName() }}</span>
                  <span class="font-mono">www.kicgle.com</span>
                </div>
              </div>
            } @else {
              <!-- 2-PAGE STANDARD ID CARD PDF PREVIEW -->
              <div class="w-full flex flex-col gap-4 animate-ios-slide select-none z-[310]">
                <!-- Info Banner -->
                <div class="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 text-white text-xs font-semibold flex items-center justify-between backdrop-blur-md border border-white/10">
                  <div class="flex items-center gap-2">
                    <mat-icon class="!text-[18px] text-sky-400">picture_as_pdf</mat-icon>
                    <span>Two-Page Standard ID Card PDF Preview (ISO/IEC 7810 ID-1 • 85.6mm × 54mm)</span>
                  </div>
                  <span class="text-[10px] font-mono text-slate-400 uppercase tracking-wider">2 Pages Landscape</span>
                </div>

                <!-- Page 1 Preview (Front) -->
                <div class="flex flex-col gap-1.5">
                  <div class="flex items-center justify-between text-xs font-bold text-white/90 px-1">
                    <span class="flex items-center gap-1.5">
                      <span class="w-2 h-2 rounded-full bg-ios-blue"></span>
                      PAGE 1 OF 2 — FRONT (Official Identity & Motto)
                    </span>
                    <span class="text-[10px] text-white/60 font-mono">85.6 mm × 54.0 mm</span>
                  </div>

                  <!-- Front Card Frame in Exact Layout -->
                  <div class="w-full rounded-2xl pt-3.5 sm:pt-4 px-5 sm:px-6 pb-3.5 sm:pb-4 bg-white border border-slate-200/90 shadow-xl overflow-hidden flex flex-col justify-between">
                    <!-- Header: Logo on Left, Centered Barcode, Large Prominent Material Fingerprint Icon in Top Right Corner (replaces contactless payment icon) -->
                    <div class="flex items-center justify-between gap-2 sm:gap-4 pb-2 border-b border-slate-100">
                      <div class="shrink-0 flex items-center">
                        <img src="kicgle.svg" alt="Kicgle" class="h-7 sm:h-8 w-auto object-contain" referrerpolicy="no-referrer" />
                      </div>

                      <div class="flex-1 flex flex-col items-center justify-center min-w-0 px-1 sm:px-2">
                        <div class="w-full max-w-[180px] sm:max-w-[210px] flex flex-col items-center">
                          <svg [attr.viewBox]="'0 0 ' + barcodeData().totalWidth + ' 22'" class="h-4 sm:h-5 w-full text-zinc-900" preserveAspectRatio="none">
                            @for (bar of barcodeData().bars; track $index) {
                              <rect [attr.x]="bar.x" y="0" [attr.width]="bar.width" height="22" fill="#18181b" />
                            }
                          </svg>
                          <span class="font-mono text-[9px] sm:text-[10px] font-bold tracking-[0.1em] text-zinc-600 mt-0.5 whitespace-nowrap text-center block w-full">
                            {{ formattedKicgleId() }}
                          </span>
                        </div>
                      </div>

                      <div class="shrink-0 flex items-center justify-end">
                        <mat-icon
                          class="fingerprint-icon-vertical !text-[34px] sm:!text-[38px] !w-[34px] !h-[34px] sm:!w-[38px] sm:!h-[38px] text-ios-blue select-none flex items-center justify-center leading-none"
                          aria-label="Biometric Fingerprint Verified"
                        >
                          fingerprint
                        </mat-icon>
                      </div>
                    </div>

                    <!-- Body -->
                    <div class="flex flex-row items-center gap-4 sm:gap-6 py-3 my-auto">
                      <div class="shrink-0">
                        <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-slate-200/90 shadow-md bg-slate-50 flex items-center justify-center">
                          @if (photoDataUrl() || photoURL()) {
                            <img [src]="photoDataUrl() || photoURL()" [alt]="displayName()" class="w-full h-full object-cover rounded-full" referrerpolicy="no-referrer" />
                          } @else {
                            <div class="w-full h-full flex items-center justify-center bg-gradient-to-tr from-ios-blue to-purple-600 text-white text-xl sm:text-2xl font-black rounded-full">
                              {{ initial() }}
                            </div>
                          }
                        </div>
                      </div>

                      <div class="flex-1 flex flex-col justify-center min-w-0 text-left">
                        <div class="mb-2">
                          <span class="block text-[10px] font-bold uppercase tracking-wider text-slate-500">NAME</span>
                          <span class="block text-base sm:text-lg font-extrabold text-slate-900 tracking-tight leading-snug break-words mt-0.5">{{ displayName() }}</span>
                        </div>
                        <div>
                          <span class="block text-[10px] font-bold uppercase tracking-wider text-slate-500">DATE OF BIRTH</span>
                          <span class="block text-sm sm:text-base font-bold text-slate-800 tracking-tight leading-snug whitespace-nowrap mt-0.5">{{ formattedDob() }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Footer -->
                    <div class="pt-2 border-t border-slate-100 w-full text-center">
                      <div class="text-[9px] sm:text-[10px] font-semibold text-zinc-600 leading-normal space-y-0.5">
                        <div>Learn clearly (<span class="text-ios-blue font-bold">Keen</span>), Think innovatively (<span class="text-ios-red font-bold">Idea</span>)</div>
                        <div>Be determinate (<span class="text-ios-orange font-bold">Core</span>), Act fast (<span class="text-ios-purple font-bold">Go</span>), Plan properly (<span class="text-ios-green font-bold">Lead</span>)</div>
                        <div>Delivery everlasting quality (<span class="text-ios-pink font-bold">Ever</span>, <span class="text-amber-500 font-bold tracking-wide underline decoration-amber-400 decoration-1 underline-offset-2 ml-0.5">SUCCESS IS CERTAIN</span>)</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Page 2 Preview (Back) -->
                <div class="flex flex-col gap-1.5 mt-2">
                  <div class="flex items-center justify-between text-xs font-bold text-white/90 px-1">
                    <span class="flex items-center gap-1.5">
                      <span class="w-2 h-2 rounded-full bg-purple-500"></span>
                      PAGE 2 OF 2 — BACK (Official Kicgle Emblem & Seal)
                    </span>
                    <span class="text-[10px] text-white/60 font-mono">85.6 mm × 54.0 mm</span>
                  </div>

                  <!-- Back Card Frame in Exact Layout -->
                  <div class="w-full rounded-2xl p-5 sm:p-7 bg-white border border-slate-200/90 shadow-xl overflow-hidden flex flex-col items-center justify-between min-h-[220px] sm:min-h-[240px]">
                    <div class="w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-slate-400 pb-2 border-b border-slate-100">
                      <div class="flex items-center gap-1.5">
                        <svg class="w-3.5 h-3.5 text-ios-blue shrink-0" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                        </svg>
                        <span>Official Identity Card</span>
                      </div>
                      <span>Card No: {{ formattedKicgleId() }}</span>
                    </div>

                    <div class="flex-1 w-full my-2 flex flex-col items-center justify-center text-center">
                      <img src="kicgle.svg" alt="Kicgle Logo" class="w-64 sm:w-72 h-16 sm:h-20 object-contain filter drop-shadow-sm" referrerpolicy="no-referrer" />
                      <p class="mt-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500 text-center">
                        Universal Digital Identity
                      </p>
                    </div>

                    <div class="w-full pt-2 border-t border-slate-100 flex items-center justify-between text-[9px] sm:text-[10px] font-bold text-slate-400">
                      <span>Issued for: {{ displayName() }}</span>
                      <span class="font-mono">www.kicgle.com</span>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- DEDICATED HIGH-PRECISION CAPTURE CONTAINERS (Exact ISO/IEC 7810 ID-1 Standard Proportions: 85.6mm x 54mm) -->
          <!-- Mounted off-screen with full paint rendering to ensure pixel-perfect html2canvas capture -->
          <div class="absolute left-[-99999px] top-0 pointer-events-none opacity-100 z-[-100] overflow-hidden" aria-hidden="true">
            <!-- PRINT CAPTURE PAGE 1: Front ID Card (Exact Replica of On-Screen Layout) -->
            <div
              #frontCardPrint
              style="width: 535px; height: 337.5px; box-sizing: border-box;"
              class="pt-4 px-6 pb-4 bg-white text-slate-900 border border-slate-200/90 rounded-2xl flex flex-col justify-between overflow-hidden shadow-none"
            >
              <!-- Header Row: Logo on Left, Centered Barcode, Large Prominent Material Fingerprint Icon in Top Right Corner (replaces contactless payment icon) -->
              <div class="flex items-center justify-between gap-3 pb-2 border-b border-slate-100">
                <div class="shrink-0 flex items-center">
                  <img
                    src="kicgle.svg"
                    alt="Kicgle"
                    class="h-8 w-auto object-contain"
                    referrerpolicy="no-referrer"
                  />
                </div>

                <div class="flex-1 flex flex-col items-center justify-center min-w-0 px-2">
                  <div class="w-full max-w-[210px] flex flex-col items-center">
                    <svg
                      [attr.viewBox]="'0 0 ' + barcodeData().totalWidth + ' 22'"
                      class="h-5 w-full text-zinc-900"
                      preserveAspectRatio="none"
                    >
                      @for (bar of barcodeData().bars; track $index) {
                        <rect
                          [attr.x]="bar.x"
                          y="0"
                          [attr.width]="bar.width"
                          height="22"
                          fill="#18181b"
                        />
                      }
                    </svg>
                    <span class="font-mono text-[10px] font-bold tracking-[0.1em] text-zinc-600 mt-0.5 whitespace-nowrap text-center block w-full">
                      {{ formattedKicgleId() }}
                    </span>
                  </div>
                </div>

                <div class="shrink-0 flex items-center justify-end">
                  <svg
                    class="fingerprint-icon-vertical w-9 h-9 text-[#007aff]"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    style="transform: none !important; -webkit-transform: none !important; rotate: 0deg !important; display: block;"
                    aria-label="Biometric Fingerprint Verified"
                  >
                    <path d="M17.81 4.47c-.08 0-.16-.02-.23-.06C15.66 3.42 14 3 12.01 3c-1.98 0-3.86.47-5.57 1.41-.24.13-.54.04-.68-.2-.13-.24-.04-.55.2-.68C7.82 2.52 9.86 2 12.01 2c2.13 0 3.99.47 5.92 1.5.24.13.33.43.2.67-.08.19-.28.3-.32.3zm2.59 4.31c-.13 0-.26-.05-.35-.15-1.57-1.61-3.69-2.5-6.04-2.5s-4.47.89-6.04 2.5c-.2.2-.51.2-.71 0s-.2-.51 0-.71c1.76-1.8 4.13-2.79 6.75-2.79 2.62 0 4.99.99 6.75 2.79.2.2.2.51 0 .71-.1.11-.23.15-.36.15zM12 8.5c-.28 0-.5.22-.5.5v5c0 .28.22.5.5.5s.5-.22.5-.5V9c0-.28-.22-.5-.5-.5zm-4.3 1.8c-.13 0-.26-.05-.35-.15-.2-.2-.2-.51 0-.71 2.56-2.56 6.74-2.56 9.3 0 .2.2.2.51 0 .71s-.51.2-.71 0c-2.17-2.17-5.71-2.17-7.88 0-.1.11-.23.15-.36.15zm8.56 3.9c-.21 0-.4-.13-.47-.33-.31-.88-.81-1.66-1.46-2.29-.2-.19-.2-.51-.01-.71.19-.2.51-.2.71-.01.76.73 1.34 1.64 1.7 2.67.09.26-.05.55-.31.64-.06.02-.11.03-.16.03zm-8.52.01c-.05 0-.11-.01-.16-.03-.26-.09-.4-.38-.31-.64.36-1.03.94-1.94 1.7-2.67.2-.19.52-.19.71.01.19.2.19.52-.01.71-.65.63-1.15 1.41-1.46 2.29-.07.2-.26.33-.47.33zm1.69 2.82c-.13 0-.26-.05-.35-.15-.2-.2-.2-.51 0-.71 1.46-1.46 3.84-1.46 5.3 0 .2.2.2.51 0 .71s-.51.2-.71 0c-1.07-1.07-2.82-1.07-3.88 0-.1.11-.23.15-.36.15zm-2.69 2.3c-.13 0-.26-.05-.35-.15-.2-.2-.2-.51 0-.71 2.94-2.94 7.74-2.94 10.68 0 .2.2.2.51 0 .71s-.51.2-.71 0c-2.55-2.55-6.72-2.55-9.26 0-.1.11-.23.15-.36.15zm10.74 2.85c-.13 0-.26-.05-.35-.15-1.12-1.12-2.6-1.74-4.19-1.74s-3.07.62-4.19 1.74c-.2.2-.51.2-.71 0s-.2-.51 0-.71c1.31-1.31 3.05-2.03 4.9-2.03s3.59.72 4.9 2.03c.2.2.2.51 0 .71-.1.1-.23.15-.36.15z"/>
                  </svg>
                </div>
              </div>

              <!-- Body Row -->
              <div class="flex flex-row items-center gap-6 py-2.5 my-auto">
                <div class="shrink-0">
                  <div class="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-200/90 shadow-md bg-slate-50 flex items-center justify-center">
                    @if (photoDataUrl() || photoURL()) {
                      <img
                        [src]="photoDataUrl() || photoURL()"
                        [alt]="displayName()"
                        class="w-full h-full object-cover rounded-full"
                        referrerpolicy="no-referrer"
                      />
                    } @else {
                      <div class="w-full h-full flex items-center justify-center bg-gradient-to-tr from-[#007aff] to-[#9333ea] text-white text-3xl font-black rounded-full">
                        {{ initial() }}
                      </div>
                    }
                  </div>
                </div>

                <div class="flex-1 flex flex-col justify-center text-left min-w-0">
                  <div class="mb-2.5">
                    <span class="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      NAME
                    </span>
                    <span class="block text-xl font-extrabold text-slate-900 tracking-tight leading-snug break-words mt-0.5">
                      {{ displayName() }}
                    </span>
                  </div>

                  <div>
                    <span class="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      DATE OF BIRTH
                    </span>
                    <span class="block text-base font-bold text-slate-800 tracking-tight leading-snug whitespace-nowrap mt-0.5">
                      {{ formattedDob() }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="pt-2.5 border-t border-slate-100 w-full text-center">
                <div class="text-[10px] font-semibold text-zinc-600 leading-normal space-y-0.5">
                  <div>
                    Learn clearly (<span class="text-[#007aff] font-bold">Keen</span>), Think innovatively (<span class="text-[#ff3b30] font-bold">Idea</span>)
                  </div>
                  <div>
                    Be determinate (<span class="text-[#ff9500] font-bold">Core</span>), Act fast (<span class="text-[#af52de] font-bold">Go</span>), Plan properly (<span class="text-[#34c759] font-bold">Lead</span>)
                  </div>
                  <div>
                    Delivery everlasting quality (<span class="text-[#ff2d55] font-bold">Ever</span>, <span class="text-[#d97706] font-bold underline decoration-[#d97706] ml-0.5">SUCCESS IS CERTAIN</span>)
                  </div>
                </div>
              </div>
            </div>

            <!-- PRINT CAPTURE PAGE 2: Back ID Card (Prominently Centered Kicgle Logo in Same ID Card Size) -->
            <div
              #backCardPrint
              style="width: 535px; height: 337.5px; box-sizing: border-box;"
              class="p-6 bg-white text-slate-900 border border-slate-200/90 rounded-2xl flex flex-col items-center justify-between overflow-hidden shadow-none text-center"
            >
              <!-- Top Strip -->
              <div class="w-full flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-slate-400 pb-2 border-b border-slate-100">
                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-[#007aff]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                  </svg>
                  <span>Official Identity Card</span>
                </div>
                <span>Card No: {{ formattedKicgleId() }}</span>
              </div>

              <!-- Prominently Centered Kicgle Logo -->
              <div class="flex-1 w-full my-2 flex flex-col items-center justify-center">
                <img
                  src="kicgle.svg"
                  alt="Kicgle Logo"
                  style="width: 320px; height: 76px;"
                  class="object-contain filter drop-shadow-xs"
                  referrerpolicy="no-referrer"
                />
                <p class="mt-2 text-[12px] font-bold uppercase tracking-[0.25em] text-slate-500 text-center">
                  Universal Digital Identity
                </p>
              </div>

              <!-- Bottom Strip -->
              <div class="w-full pt-2.5 border-t border-slate-100 flex items-center justify-between text-[10px] font-bold text-slate-400">
                <span>Issued for: {{ displayName() }}</span>
                <span class="font-mono">www.kicgle.com</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class KicgleIdCard {
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);

  @ViewChild('frontCardPrint') frontCardPrint?: ElementRef<HTMLDivElement>;
  @ViewChild('backCardPrint') backCardPrint?: ElementRef<HTMLDivElement>;

  isOpen = input<boolean>(false);
  profile = input<AuthProfile | null>(null);
  closed = output<void>();

  activeSide = signal<'front' | 'back' | 'preview'>('front');
  isGeneratingPdf = signal<boolean>(false);
  photoDataUrl = signal<string | null>(null);

  constructor() {
    // Keep photoDataUrl synced for clean canvas rendering
    effect(() => {
      const url = this.photoURL();
      if (url) {
        this.loadPhotoDataUrl(url);
      } else {
        this.photoDataUrl.set(null);
      }
    });
  }

  private loadPhotoDataUrl(url: string) {
    if (!url || url.startsWith('data:')) {
      this.photoDataUrl.set(url || null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 200;
        canvas.height = img.naturalHeight || 200;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          this.photoDataUrl.set(canvas.toDataURL('image/png'));
          return;
        }
      } catch {
        // Fallback to original url if canvas is tainted
      }
      this.photoDataUrl.set(url);
    };
    img.onerror = () => {
      this.photoDataUrl.set(url);
    };
    img.src = url;
  }

  // Dynamically pulled from individual user's database record or active auth profile
  currentProfile = computed(() => this.profile() || this.authService.userProfile());

  // 17-digit unique number dynamically linked to the user's account database record
  kicgleId = computed(() => {
    const p = this.currentProfile();
    // 1. Direct from individual user's database record (Firestore 'kicgle' field)
    if (p?.kicgle && p.kicgle.trim().length === 17) {
      return p.kicgle.trim();
    }
    if (p?.kicgle && /^\d+$/.test(p.kicgle)) {
      return p.kicgle.slice(0, 17).padStart(17, "0");
    }

    // 2. Deterministic unique 17-digit number linked to the individual user's account UID
    const uid = p?.uid || this.authService.currentUser?.uid || "kic9le";
    let hash1 = 5381;
    let hash2 = 52711;
    for (let i = 0; i < uid.length; i++) {
      const char = uid.charCodeAt(i);
      hash1 = ((hash1 << 5) + hash1) ^ char;
      hash2 = ((hash2 << 5) + hash2) ^ char;
    }
    const hStr = Math.abs(hash1).toString() + Math.abs(hash2).toString();
    return (hStr + "10374048404561234").slice(0, 17);
  });

  // Formatted 17-digit string: 4-4-1-4-4
  formattedKicgleId = computed(() => {
    const id = this.kicgleId();
    if (id.length !== 17) return id;
    return `${id.slice(0, 4)} ${id.slice(4, 8)} ${id.slice(8, 9)} ${id.slice(9, 13)} ${id.slice(13, 17)}`;
  });

  // User's name dynamically pulled from database record
  displayName = computed(() => {
    const p = this.currentProfile();
    return p?.displayName || this.authService.currentUser?.displayName || "Kicgle";
  });

  // User's photo dynamically pulled from database record
  photoURL = computed(() => {
    const p = this.currentProfile();
    return p?.photoURL || p?.photo || this.authService.currentUser?.photoURL || "";
  });

  initial = computed(() => {
    const name = this.displayName();
    return (name.charAt(0) || "K").toUpperCase();
  });

  // Date of birth dynamically pulled from individual user's database record
  formattedDob = computed(() => {
    const p = this.currentProfile();
    const dob = p?.dob;
    if (!dob) return "17 Jan 1994";
    try {
      let dateObj: Date;
      if (dob && typeof (dob as { toDate?: () => Date }).toDate === "function") {
        dateObj = (dob as { toDate: () => Date }).toDate();
      } else if (dob instanceof Date) {
        dateObj = dob;
      } else if (typeof dob === "string" || typeof dob === "number") {
        dateObj = new Date(dob);
      } else {
        return "17 Jan 1994";
      }
      if (isNaN(dateObj.getTime())) return "17 Jan 1994";
      return dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "17 Jan 1994";
    }
  });

  // Barcode dynamically generated directly from the user's 17-digit database account number
  barcodeData = computed(() => {
    const id = this.kicgleId();
    const bars: { x: number; width: number }[] = [];
    let curX = 0;

    // Start guard bars
    bars.push({ x: curX, width: 2 });
    curX += 4;
    bars.push({ x: curX, width: 1 });
    curX += 3;

    for (const char of id) {
      const digit = parseInt(char, 10) || 0;
      const w1 = (digit % 3) + 1;
      const w2 = ((digit >> 1) % 2) + 1;
      bars.push({ x: curX, width: w1 });
      curX += w1 + 1.5;
      bars.push({ x: curX, width: w2 });
      curX += w2 + 1.5;
    }

    // Stop guard bars
    bars.push({ x: curX, width: 2 });
    curX += 3;
    bars.push({ x: curX, width: 2 });
    curX += 2;

    return { bars, totalWidth: curX };
  });

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.closed.emit();
    }
  }

  /**
   * Generates and downloads a two-page standard ID card PDF:
   * Page One: Exact replica of the current front ID card layout
   * Page Two: Prominently centered Kicgle logo in the same ID card size
   */
  async downloadPdf() {
    if (this.isGeneratingPdf()) return;
    this.isGeneratingPdf.set(true);

    try {
      const frontEl = this.frontCardPrint?.nativeElement;
      const backEl = this.backCardPrint?.nativeElement;

      if (!frontEl || !backEl) {
        throw new Error("ID card elements are not ready for PDF export.");
      }

      // Small delay to ensure images/fonts are painted
      await new Promise((resolve) => setTimeout(resolve, 200));

      const captureOptions = {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      };

      // Standard ISO/IEC 7810 ID-1 card dimensions: 85.6 mm x 54.0 mm
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: [85.6, 54],
      });

      // Capture Page 1: Exact replica of the current front ID card layout
      const canvasFront = await html2canvas(frontEl, captureOptions);
      const imgFront = canvasFront.toDataURL("image/jpeg", 0.98);
      pdf.addImage(imgFront, "JPEG", 0, 0, 85.6, 54, undefined, "FAST");

      // Capture Page 2: Same ID card size with prominently centered Kicgle logo
      pdf.addPage([85.6, 54], "landscape");
      const canvasBack = await html2canvas(backEl, captureOptions);
      const imgBack = canvasBack.toDataURL("image/jpeg", 0.98);
      pdf.addImage(imgBack, "JPEG", 0, 0, 85.6, 54, undefined, "FAST");

      const safeName = (this.displayName() || "User").replace(/[^a-zA-Z0-9]/g, "_");
      const fileName = `Kicgle_ID_Card_${safeName}_${this.kicgleId()}.pdf`;
      pdf.save(fileName);

      this.notificationService.show("Two-page standard ID card PDF downloaded successfully!", "success");
    } catch (err: unknown) {
      console.error("Error generating ID card PDF:", err);
      const msg = err instanceof Error ? err.message : "Failed to generate PDF. Please try again.";
      this.notificationService.show(msg, "error");
    } finally {
      this.isGeneratingPdf.set(false);
    }
  }
}
